import React, { useRef, useState } from "react";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import { ItemBasicResponse } from "../../types/medicineSearch.types";
import { ConsultRfzoDraftItem } from "../../contexts/ConsultOrderContext";
import styles from "./ConsultRfzoItemDialog.module.scss";

// ITAM-2614: quantity + participation entry for one therapy item, stacked on
// top of the RFZO form modal (overlay 1010, like ItemStockOverviewModal).
// Prop-driven re-implementation of RFZOArticleModal — it calls onAdd(line)
// instead of dispatching to the global rfzoTherapy slice (the consult draft
// must never collide with a simultaneously open classic RFZO zahtev box).

export interface ConsultRfzoItemDialogProps {
  /** The dropdown selection (global /search/item result). */
  item: ItemBasicResponse;
  onAdd: (line: ConsultRfzoDraftItem) => void;
  onClose: () => void;
}

// -1 marks the "Prilagodi" (custom amount) option.
const CUSTOM_PARTICIPATION = "-1";

const ConsultRfzoItemDialog: React.FC<ConsultRfzoItemDialogProps> = ({
  item,
  onAdd,
  onClose,
}) => {
  const [quantity, setQuantity] = useState("1");
  const [participationChoice, setParticipationChoice] = useState("0");
  const [customParticipation, setCustomParticipation] = useState("0");
  const [error, setError] = useState<string | null>(null);

  const quantityRef = useRef<HTMLInputElement>(null);

  const titleId = "consult-rfzo-item-dialog-title";

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const qty = Number(quantity);
    // D5: rfzoQuantity is required and must be a positive integer.
    if (!Number.isInteger(qty) || qty < 1) {
      setError("Količina mora biti ceo broj veći od nule.");
      return;
    }
    const rawParticipation =
      participationChoice === CUSTOM_PARTICIPATION
        ? customParticipation
        : participationChoice;
    const participation = Number(parseFloat(rawParticipation || "0").toFixed(2));
    // D5: rfzoParticipationPrice is required; a genuine 0 is valid.
    if (!Number.isFinite(participation) || participation < 0) {
      setError("Participacija ne može biti negativna.");
      return;
    }
    onAdd({
      itemId: item.id,
      name: item.name,
      rfzoQuantity: qty,
      rfzoParticipationPrice: participation,
    });
    onClose();
  };

  return (
    <AccessibleDialog
      isOpen={true}
      onRequestClose={onClose}
      titleId={titleId}
      initialFocusRef={quantityRef}
      maxWidth={420}
      overlayZIndex={1010}
    >
      <div className={styles.body}>
        <h2 id={titleId} className={styles.title}>
          Dodavanje artikla za RFZO
        </h2>
        <p className={styles.itemName}>{item.name}</p>
        <form onSubmit={submit} className={styles.form}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="consult-rfzo-item-qty">
              Količina
            </label>
            <input
              id="consult-rfzo-item-qty"
              ref={quantityRef}
              className={styles.input}
              type="number"
              step="1"
              min={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              onFocus={(e) => e.target.select()}
            />
          </div>
          <div className={styles.field}>
            <label
              className={styles.label}
              htmlFor="consult-rfzo-item-participation"
            >
              Participacija / Učešće
            </label>
            <select
              id="consult-rfzo-item-participation"
              className={styles.input}
              value={participationChoice}
              onChange={(e) => setParticipationChoice(e.target.value)}
            >
              <option value="0">0.00</option>
              <option value="50">50 RSD</option>
              <option value={CUSTOM_PARTICIPATION}>Prilagodi</option>
            </select>
            {participationChoice === CUSTOM_PARTICIPATION && (
              <input
                className={styles.input}
                type="number"
                step="any"
                min={0}
                value={customParticipation}
                onChange={(e) => setCustomParticipation(e.target.value)}
                onFocus={(e) => e.target.select()}
                aria-label="Prilagođena participacija"
              />
            )}
          </div>
          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.secondary}
              onClick={onClose}
            >
              Otkaži
            </button>
            <button type="submit" className={styles.primary}>
              Dodaj
            </button>
          </div>
        </form>
      </div>
    </AccessibleDialog>
  );
};

export default ConsultRfzoItemDialog;
